import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Toaster, toast } from 'sonner';
import {
  Search,
  MapPin,
  Download,
  Save,
  Loader2,
  CheckCircle2,
  XCircle,
  Globe,
  Phone,
  Mail,
  Star,
  Clock,
  ArrowLeft,
  Play,
  History,
  Building2,
  ExternalLink,
  Linkedin,
  User,
  AtSign,
  FileText
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ScraperPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [query, setQuery] = useState('');
  const [maxResults, setMaxResults] = useState(20);
  const [currentJob, setCurrentJob] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [polling, setPolling] = useState(false);

  // Esempi di query
  const queryExamples = [
    "Studi architettura Bergamo",
    "Aziende metalmeccaniche Dalmine",
    "Cliniche dentistiche Seriate",
    "Ristoranti Bergamo centro",
    "Commercialisti Treviglio",
    "Palestre Bergamo"
  ];

  const fetchHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/scraper/history`);
      setHistory(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Polling per lo stato del job
  useEffect(() => {
    let interval;
    if (currentJob && polling) {
      interval = setInterval(async () => {
        try {
          const response = await axios.get(`${API_URL}/scraper/status/${currentJob}`);
          setJobStatus(response.data);
          
          if (response.data.status === 'completed' || response.data.status === 'failed') {
            setPolling(false);
            fetchHistory();
            if (response.data.status === 'completed') {
              toast.success(`Scraping completato! ${response.data.total} risultati trovati`);
            } else {
              toast.error(`Errore: ${response.data.error}`);
            }
          }
        } catch (error) {
          console.error('Error polling status:', error);
        }
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [currentJob, polling, fetchHistory]);

  const startScraping = async () => {
    if (!query.trim()) {
      toast.error('Inserisci una query di ricerca');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/scraper/start`, {
        query: query.trim(),
        max_results: maxResults
      });
      
      setCurrentJob(response.data.job_id);
      setJobStatus({ status: 'pending', query: query.trim(), results: [] });
      setPolling(true);
      toast.info(`Scraping avviato per: ${query}`);
    } catch (error) {
      toast.error('Errore nell\'avvio dello scraping');
    }
  };

  const exportCSV = async (jobId) => {
    try {
      const response = await axios.get(`${API_URL}/scraper/results/${jobId}/csv`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `scrape_results_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV scaricato!');
    } catch (error) {
      toast.error('Errore nel download del CSV');
    }
  };

  const exportEmailsCSV = async (jobId) => {
    try {
      const response = await axios.get(`${API_URL}/scraper/results/${jobId}/emails-csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', `emails_${new Date().toISOString().split('T')[0]}.csv`);
      a.click();
      toast.success('Email esportate in CSV!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Nessuna email da esportare');
    }
  };

  const exportEmailsTXT = async (jobId) => {
    try {
      const response = await axios.get(`${API_URL}/scraper/results/${jobId}/emails-txt`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', `emails_${new Date().toISOString().split('T')[0]}.txt`);
      a.click();
      toast.success('Lista email scaricata!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Nessuna email da esportare');
    }
  };

  const saveToLeads = async (jobId) => {
    try {
      const response = await axios.post(`${API_URL}/scraper/save-to-leads/${jobId}`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Errore nel salvataggio dei lead');
    }
  };

  const loadJobResults = async (jobId) => {
    try {
      const response = await axios.get(`${API_URL}/scraper/status/${jobId}`);
      setJobStatus(response.data);
      setCurrentJob(jobId);
    } catch (error) {
      toast.error('Errore nel caricamento dei risultati');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-rose-500" />;
      case 'running':
      case 'enriching':
      case 'enriching_web':
      case 'enriching_hunter':
      case 'pending':
        return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'In attesa...';
      case 'running': return 'Scraping Google Maps...';
      case 'enriching_web': return 'Estrazione email dai siti...';
      case 'enriching_hunter': return 'Hunter.io - Email verificate...';
      case 'enriching': return 'Arricchimento dati...';
      case 'completed': return 'Completato';
      case 'failed': return 'Fallito';
      default: return status;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/dashboard')}
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center">
                <MapPin className="w-5 h-5" />
              </div>
              <span className="font-bold text-xl text-slate-900 tracking-tight">Google Maps Scraper</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pannello Ricerca */}
          <div className="lg:col-span-1 space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Search className="w-5 h-5" />
                    Nuova Ricerca
                  </CardTitle>
                  <CardDescription>
                    Cerca aziende su Google Maps nella tua zona
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Query di ricerca</Label>
                    <Textarea
                      placeholder="es. Studi architettura Bergamo"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      className="min-h-[80px]"
                      data-testid="scraper-query-input"
                    />
                    <p className="text-xs text-slate-500">
                      Includi tipo di attività + località per risultati migliori
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Esempi rapidi</Label>
                    <div className="flex flex-wrap gap-2">
                      {queryExamples.slice(0, 4).map((example, idx) => (
                        <Badge 
                          key={idx}
                          variant="outline"
                          className="cursor-pointer hover:bg-slate-100"
                          onClick={() => setQuery(example)}
                        >
                          {example}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Numero massimo risultati</Label>
                    <Select value={String(maxResults)} onValueChange={(v) => setMaxResults(Number(v))}>
                      <SelectTrigger data-testid="max-results-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10 risultati</SelectItem>
                        <SelectItem value="20">20 risultati</SelectItem>
                        <SelectItem value="50">50 risultati</SelectItem>
                        <SelectItem value="100">100 risultati</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button 
                    className="w-full" 
                    onClick={startScraping}
                    disabled={polling || !query.trim()}
                    data-testid="start-scraping-btn"
                  >
                    {polling ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Scraping in corso...
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Avvia Scraping
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Storico */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <History className="w-4 h-4" />
                    Ricerche Recenti
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {history.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">
                      Nessuna ricerca effettuata
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {history.slice(0, 10).map((item) => (
                        <div 
                          key={item.id}
                          className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
                          onClick={() => loadJobResults(item.id)}
                        >
                          <div className="truncate flex-1 mr-2">
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {item.query}
                            </p>
                            <p className="text-xs text-slate-500">
                              {new Date(item.created_at).toLocaleDateString('it-IT')}
                            </p>
                          </div>
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Pannello Risultati */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
            >
              <Card className="border-slate-200 min-h-[600px]">
                <CardHeader className="border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Risultati</CardTitle>
                      {jobStatus && (
                        <CardDescription className="flex items-center gap-2 mt-1">
                          {getStatusIcon(jobStatus.status)}
                          {getStatusLabel(jobStatus.status)}
                          {jobStatus.total > 0 && ` - ${jobStatus.total} aziende trovate`}
                        </CardDescription>
                      )}
                    </div>
                    
                    {jobStatus?.status === 'completed' && jobStatus?.results?.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => exportCSV(currentJob)}
                          data-testid="export-csv-btn"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Export CSV
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportEmailsCSV(currentJob)}
                          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          data-testid="export-emails-csv-btn"
                        >
                          <Mail className="w-4 h-4 mr-2" /> Email CSV
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportEmailsTXT(currentJob)}
                          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          data-testid="export-emails-txt-btn"
                        >
                          <FileText className="w-4 h-4 mr-2" /> Email TXT
                        </Button>
                        <Button 
                          size="sm"
                          onClick={() => saveToLeads(currentJob)}
                          data-testid="save-to-leads-btn"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          Salva come Lead
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* Progress bar durante lo scraping */}
                  {polling && jobStatus && (
                    <div className="mt-4">
                      <Progress 
                        value={jobStatus.progress ? (jobStatus.progress / jobStatus.total) * 100 : 10} 
                        className="h-2"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        {jobStatus.progress || 0} di {jobStatus.total} elaborati
                      </p>
                    </div>
                  )}
                </CardHeader>
                
                <CardContent className="p-0">
                  {!jobStatus ? (
                    <div className="flex flex-col items-center justify-center py-16 px-4">
                      <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                        <MapPin className="w-10 h-10 text-slate-400" />
                      </div>
                      <h3 className="text-lg font-medium text-slate-900 mb-2">
                        Pronto per lo scraping
                      </h3>
                      <p className="text-slate-500 text-center max-w-md">
                        Inserisci una query di ricerca a sinistra per trovare aziende 
                        su Google Maps nella zona di Bergamo e dintorni
                      </p>
                    </div>
                  ) : jobStatus.results?.length === 0 && jobStatus.status !== 'completed' ? (
                    <div className="flex flex-col items-center justify-center py-16">
                      <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
                      <p className="text-slate-600">
                        {getStatusLabel(jobStatus.status)}
                      </p>
                      <p className="text-sm text-slate-500 mt-2">
                        Questa operazione può richiedere alcuni minuti...
                      </p>
                    </div>
                  ) : jobStatus.results?.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16">
                      <XCircle className="w-12 h-12 text-slate-400 mb-4" />
                      <p className="text-slate-600">Nessun risultato trovato</p>
                      <p className="text-sm text-slate-500 mt-2">
                        Prova con una query diversa o una località più ampia
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
                      {jobStatus.results.map((result, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: idx * 0.02 }}
                          className="p-4 hover:bg-slate-50"
                          data-testid={`result-item-${idx}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium text-slate-900">
                                  {result.name}
                                </h3>
                                {result.rating && (
                                  <Badge variant="outline" className="text-xs">
                                    <Star className="w-3 h-3 mr-1 fill-amber-400 text-amber-400" />
                                    {result.rating}
                                  </Badge>
                                )}
                              </div>
                              
                              {result.category && (
                                <p className="text-sm text-slate-500 mt-1">
                                  {result.category}
                                </p>
                              )}
                              
                              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-slate-600">
                                {result.address && (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {result.address.substring(0, 50)}...
                                  </span>
                                )}
                                {result.phone && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {result.phone}
                                  </span>
                                )}
                              </div>
                              
                              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm">
                                {result.website && (
                                  <a 
                                    href={result.website.startsWith('http') ? result.website : `https://${result.website}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
                                  >
                                    <Globe className="w-3 h-3" />
                                    Sito web
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                )}
                                
                                {/* Email Hunter.io VERIFICATA (prioritaria) */}
                                {result.hunter_best_email && (
                                  <span className="flex items-center gap-1 text-emerald-600 font-semibold">
                                    <CheckCircle2 className="w-3 h-3" />
                                    <Mail className="w-3 h-3" />
                                    {result.hunter_best_email}
                                    <Badge variant="outline" className="ml-1 text-[10px] py-0 px-1 bg-emerald-50 text-emerald-700 border-emerald-200">
                                      verificata
                                    </Badge>
                                  </span>
                                )}
                                
                                {/* Email da sito web (fallback) */}
                                {!result.hunter_best_email && (result.enrichment?.emails_found?.length > 0 || result.email) && (
                                  <span className="flex items-center gap-1 text-slate-600">
                                    <Mail className="w-3 h-3" />
                                    {result.email || result.enrichment?.emails_found?.[0]}
                                  </span>
                                )}
                              </div>
                              
                              {/* Decision Maker da Hunter.io */}
                              {result.hunter_decision_maker?.name && (
                                <div className="flex items-center gap-2 mt-2 p-2 bg-emerald-50 rounded-lg border border-emerald-100">
                                  <User className="w-4 h-4 text-emerald-600" />
                                  <div className="text-sm">
                                    <span className="font-medium text-emerald-800">{result.hunter_decision_maker.name}</span>
                                    {result.hunter_decision_maker.position && (
                                      <span className="text-emerald-600 ml-2">• {result.hunter_decision_maker.position}</span>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              {/* LinkedIn Links */}
                              {result.enrichment && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {result.enrichment.linkedin_company_url && (
                                    <a 
                                      href={result.enrichment.linkedin_company_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100"
                                    >
                                      <Linkedin className="w-3 h-3" />
                                      Profilo Aziendale
                                    </a>
                                  )}
                                  {result.enrichment.linkedin_search_url && !result.enrichment.linkedin_company_url && (
                                    <a 
                                      href={result.enrichment.linkedin_search_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100"
                                    >
                                      <Linkedin className="w-3 h-3" />
                                      Cerca Azienda
                                    </a>
                                  )}
                                  {result.enrichment.linkedin_people_search && (
                                    <a 
                                      href={result.enrichment.linkedin_people_search}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-700 rounded text-xs hover:bg-indigo-100"
                                    >
                                      <User className="w-3 h-3" />
                                      Cerca Dipendenti
                                    </a>
                                  )}
                                  {result.enrichment.pec_probable && (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-700 rounded text-xs">
                                      <AtSign className="w-3 h-3" />
                                      PEC: {result.enrichment.pec_probable}
                                    </span>
                                  )}
                                </div>
                              )}
                              
                              {/* Altre email trovate */}
                              {result.enrichment?.emails_found?.length > 1 && (
                                <div className="mt-2 text-xs text-slate-500">
                                  Altre email: {result.enrichment.emails_found.slice(1, 4).join(', ')}
                                </div>
                              )}
                            </div>
                            
                            {result.reviews_count && (
                              <Badge variant="secondary" className="ml-2">
                                {result.reviews_count} recensioni
                              </Badge>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ScraperPage;
